import { ReactNode, useRef, useState } from "react";
import { Plus, File as FileIcon, Link as LinkIcon, X, Globe, FileUp } from "lucide-react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@libs/zapi-ui/shadcn";
import { FilePlusIcon, GlobeHemisphereWestIcon } from "@phosphor-icons/react";
import {
  DropdownMenu as DsDropdownMenu,
  DropdownMenuContent as DsDropdownMenuContent,
  DropdownMenuItem as DsDropdownMenuItem,
  DropdownMenuTrigger as DsDropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { ChatAttachButton, useChatAppearance } from "@/zapi-frontend/shared/ui/chat";
import { AddWebLinkModal } from "./AddWebLinkModal";

type TAttachMenuProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled: boolean;
  onFiles: () => void;
  onWebLink: () => void;
};

function AttachMenu({ open, onOpenChange, disabled, onFiles, onWebLink }: TAttachMenuProps) {
  const appearance = useChatAppearance();
  if (appearance === "leo")
    return (
      <DsDropdownMenu open={open} onOpenChange={(next) => onOpenChange(next)}>
        <DsDropdownMenuTrigger
          render={
            <ChatAttachButton label="Attach" disabled={disabled} data-testid="staged-attachments-attach-trigger" />
          }
        />
        <DsDropdownMenuContent side="top" align="start" sideOffset={8} className="w-auto rounded-xl p-1.5 shadow-lg">
          <DsDropdownMenuItem
            className="typo-label-md gap-2.5 rounded-lg px-2.5 py-2"
            onClick={onFiles}
            data-testid="staged-attachments-add-files-item"
          >
            <FilePlusIcon className="size-[18px] text-muted-foreground" />
            Files
          </DsDropdownMenuItem>
          <DsDropdownMenuItem
            className="typo-label-md gap-2.5 rounded-lg px-2.5 py-2"
            onClick={onWebLink}
            data-testid="staged-attachments-add-web-link-item"
          >
            <GlobeHemisphereWestIcon className="size-[18px] text-muted-foreground" />
            Web Link
          </DsDropdownMenuItem>
        </DsDropdownMenuContent>
      </DsDropdownMenu>
    );

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label="Attach"
          data-testid="staged-attachments-attach-trigger"
          className={cn(
            "h-6 flex items-center justify-center px-2 rounded-md bg-innoveraZinc-100 text-innoveraZinc-900",
            "hover:bg-innoveraZinc-150 disabled:opacity-50 disabled:pointer-events-none"
          )}
        >
          <Plus className="size-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[102px] p-1" side="top" sideOffset={4}>
        <DropdownMenuItem
          onSelect={onFiles}
          className="gap-1 px-2 py-1.5 text-[12px]"
          data-testid="staged-attachments-add-files-item"
        >
          <FileUp className="size-3" />
          Files
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={onWebLink}
          className="gap-1 px-2 py-1.5 text-[12px]"
          data-testid="staged-attachments-add-web-link-item"
        >
          <Globe className="size-3" />
          Web Link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type TStagedAttachments = {
  hasAttachments: boolean;
  chips: ReactNode;
  attachButton: ReactNode;
  overlays: ReactNode;
  take: () => { files: File[]; links: string[] };
};

export function useStagedAttachments({ disabled = false }: { disabled?: boolean } = {}): TStagedAttachments {
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [stagedLinks, setStagedLinks] = useState<string[]>([]);
  const [isAttachMenuOpen, setIsAttachMenuOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasAttachments = stagedFiles.length > 0 || stagedLinks.length > 0;

  const removeAt = (kind: "file" | "link", index: number) => {
    if (kind === "file") setStagedFiles((prev) => prev.filter((_, i) => i !== index));
    else setStagedLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const chip = (kind: "file" | "link", label: string, index: number) => (
    <div key={`${kind}-${index}`} className="flex gap-2 items-center w-full">
      <div className="flex flex-1 gap-2 items-center min-w-0">
        {kind === "file" ? (
          <FileIcon className="size-4 shrink-0 text-innoveraZinc-900" />
        ) : (
          <LinkIcon className="size-4 shrink-0 text-innoveraZinc-900" />
        )}
        <FigmaTypography variant="body-2" color="zinc900" className="truncate">
          {label}
        </FigmaTypography>
      </div>
      <button
        type="button"
        onClick={() => removeAt(kind, index)}
        aria-label="Remove"
        data-testid={`staged-attachment-${kind}-${index}-remove-button`}
        className="size-4 shrink-0 flex items-center justify-center text-innoveraZinc-500 hover:text-innoveraZinc-900"
      >
        <X className="size-4" />
      </button>
    </div>
  );

  const chips = hasAttachments ? (
    <div className="flex flex-col gap-1.5 px-3 pt-1">
      {stagedFiles.map((file, idx) => chip("file", file.name, idx))}
      {stagedLinks.map((link, idx) => chip("link", link, idx))}
    </div>
  ) : null;

  const pickFiles = () => {
    setIsAttachMenuOpen(false);
    fileInputRef.current?.click();
  };
  const openLinkModal = () => {
    setIsAttachMenuOpen(false);
    setIsLinkModalOpen(true);
  };

  const attachButton = (
    <AttachMenu
      open={isAttachMenuOpen}
      onOpenChange={setIsAttachMenuOpen}
      disabled={disabled}
      onFiles={pickFiles}
      onWebLink={openLinkModal}
    />
  );

  const overlays = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.pptx,.jpg,.jpeg,.png,.txt,.csv,.xlsx,.xls,.xlsm,.docx"
        onChange={(e) => {
          if (!e.target.files || e.target.files.length === 0) return;
          const picked = Array.from(e.target.files);
          setStagedFiles((prev) => [...prev, ...picked]);
          e.target.value = "";
        }}
        style={{ display: "none" }}
        data-testid="staged-attachments-file-input"
      />
      <AddWebLinkModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        onSubmit={(url) => setStagedLinks((prev) => [...prev, url])}
      />
    </>
  );

  const take = () => {
    const result = { files: stagedFiles.slice(), links: stagedLinks.slice() };
    setStagedFiles([]);
    setStagedLinks([]);
    return result;
  };

  return { hasAttachments, chips, attachButton, overlays, take };
}
